import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import MaterialIcon from "../MaterialIcon";
import ChatAvatar from "./ChatAvatar";
import { callAPI } from "../../helper/apiUtils";
import { BASE_URL } from "../../helper/constants";
import LanguageContext from "../../helper/LanguageContext";
import "./chat-pro.css";

// "Message info" of the mobile Pro chat (v2/pro/ChatSheetsPro.ProMsgInfoSheet + ChatAction.MsgInfoModal),
// shown as a dialog over the chat instead of replacing it:
//   1:1    one status: "Read" (done_all, blue) or "Delivered" (done) with the contact and the time
//   group  "Read by" and "Delivered to" cards of 36 avatar · name · time, "…" while not everyone is in
// GET /message/:id/info → messageInfo.readByMembers [{ id, fullName, avatar, isRead, isReached,
// readDateTime, reachDateTime }].

const uniqueById = (list) => list.filter((value, index, self) => index === self.findIndex((x) => String(x.id) === String(value.id)));

function shortTime(time, labels) {
  if (!time) return null;
  const m = moment(time);
  if (!m.isValid()) return null;
  if (m.isSame(moment(), "day")) return `${labels.today} ${m.format("HH:mm")}`;
  if (m.isSame(moment().subtract(1, "day"), "day")) return `${labels.yesterday} ${m.format("HH:mm")}`;
  return m.format("DD.MM.YY HH:mm");
}

const avatarSrc = (path) => (path ? (/^(https?:|blob:|data:)/.test(String(path)) ? path : BASE_URL + path) : null);

export default function MessageInfoSheet({ chatMessage, handleClose, isGroup = false, isInternal = false, peer, memberCount }) {
  const { isGerm, strings } = React.useContext(LanguageContext);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const labels = {
    title: strings.message_info || (isGerm ? "Nachrichteninfo" : "Message info"),
    close: strings.close || (isGerm ? "Schließen" : "Close"),
    readBy: strings.read_by || (isGerm ? "Gelesen von" : "Read by"),
    deliveredTo: strings.deliver_to || (isGerm ? "Zugestellt an" : "Delivered to"),
    read: strings.chat_info_read || (isGerm ? "Gelesen" : "Read"),
    delivered: strings.chat_info_delivered || (isGerm ? "Zugestellt" : "Delivered"),
    pending: strings.chat_info_pending || (isGerm ? "Noch nicht zugestellt." : "Not delivered yet."),
    today: strings.today || (isGerm ? "Heute" : "Today"),
    yesterday: strings.yesterday || (isGerm ? "Gestern" : "Yesterday"),
  };

  useEffect(() => {
    let live = true;
    const id = chatMessage && chatMessage.id;
    if (!id) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    callAPI(`/message/${id}/info`, {}, "GET")
      .then(({ data }) => {
        if (!live) return;
        const list = data && data.status && data.messageInfo && Array.isArray(data.messageInfo.readByMembers) ? data.messageInfo.readByMembers : [];
        setMembers(list);
      })
      .catch(() => {})
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [chatMessage]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose]);

  const groups = [];
  if (isGroup) {
    const read = uniqueById(members.filter((m) => m && m.isRead));
    const delivered = uniqueById(members.filter((m) => m && !m.isRead && m.isReached));
    const reached = uniqueById(members.filter((m) => m && (m.isReached || m.isRead)));
    const others = Number(memberCount) > 0 ? Number(memberCount) : members.length;
    if (read.length) {
      groups.push({
        key: "read",
        title: labels.readBy,
        icon: "done_all",
        tone: "is-read",
        rows: read.map((m) => ({ key: String(m.id), name: m.fullName, src: avatarSrc(m.avatar), time: shortTime(m.readDateTime, labels) })),
        more: others - read.length > 0,
      });
    }
    if (delivered.length) {
      groups.push({
        key: "delivered",
        title: labels.deliveredTo,
        icon: "done",
        tone: "",
        rows: delivered.map((m) => ({ key: String(m.id), name: m.fullName, src: avatarSrc(m.avatar), time: shortTime(m.reachDateTime, labels) })),
        more: others - reached.length > 0,
      });
    }
  } else if (members.length) {
    const first = members[0];
    const who = { key: "peer", name: (peer && peer.name) || first.fullName || "", src: (peer && peer.src) || avatarSrc(first.avatar) };
    if (first.readDateTime || first.isRead) {
      groups.push({ key: "read", title: labels.read, icon: "done_all", tone: "is-read", rows: [{ ...who, time: shortTime(first.readDateTime, labels) }] });
    } else if (first.reachDateTime || first.isReached) {
      groups.push({ key: "delivered", title: labels.delivered, icon: "done", tone: "", rows: [{ ...who, time: shortTime(first.reachDateTime, labels) }] });
    }
  }

  return createPortal(
    <div className="kjc-sheet-overlay" onMouseDown={(e) => e.target === e.currentTarget && handleClose()}>
      <div className={`kjc-sheet${isInternal ? " kjc-internal" : ""}`} role="dialog" aria-modal="true" aria-label={labels.title}>
        <div className="kjc-sheet__head">
          <div className="kjc-sheet__title">{labels.title}</div>
          <button type="button" className="kjc-sheet__close" onClick={handleClose} aria-label={labels.close} title={labels.close}>
            <MaterialIcon icon="close" size={18} weight={400} />
          </button>
        </div>
        <div className="kjc-sheet__body">
          {loading ? (
            <div className="kjc-info-group" aria-busy="true">
              <span className="kjc-skeleton kjc-skeleton--caption" />
              <div className="kjc-info-card">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="kjc-info-row">
                    <span className="kjc-skeleton kjc-skeleton--avatar36" />
                    <span className="kjc-skeleton kjc-skeleton--line" />
                  </div>
                ))}
              </div>
            </div>
          ) : groups.length ? (
            groups.map((g) => (
              <div key={g.key} className="kjc-info-group">
                <div className={`kjc-info-caption ${g.tone}`.trim()}>
                  <MaterialIcon icon={g.icon} size={16} weight={400} />
                  <span>{g.title}</span>
                </div>
                <div className="kjc-info-card">
                  {g.rows.map((r) => (
                    <div key={r.key} className="kjc-info-row">
                      <ChatAvatar size={36} name={r.name} src={r.src} />
                      <span className="kjc-info-row__name">{r.name}</span>
                      <span className="kjc-info-row__time">{r.time || "—"}</span>
                    </div>
                  ))}
                  {g.more ? (
                    <div className="kjc-info-row kjc-info-row--more" aria-hidden="true">
                      <MaterialIcon icon="more_horiz" size={22} weight={400} />
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          ) : (
            <div className="kjc-sheet__empty">{labels.pending}</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
